import { supabase } from '@/integrations/supabase/client';

export interface AICreditsStatus {
  monthly_allowance: number;
  credits_used: number;
  credits_remaining: number;
  purchased_credits: number;
  reset_date: string;
}

export interface AICreditsUsage {
  id: string;
  workspace_id: string;
  user_id: string;
  credits_used: number;
  operation_type: string;
  operation_details: Record<string, any>;
  created_at: string;
}

class AICreditsService {
  /**
   * Get AI credits status for a workspace
   */
  async getCreditsStatus(workspaceId: string): Promise<AICreditsStatus | null> {
    try {
      const { data, error } = await (supabase as any)
        .rpc('get_workspace_ai_credits_status', { workspace_uuid: workspaceId });

      if (error) {
        console.error('Error fetching AI credits status:', error);
        return null;
      }

      return data?.[0] || null;
    } catch (error) {
      console.error('Error in getCreditsStatus:', error);
      return null;
    }
  }

  /**
   * Use AI credits for an operation
   */
  async useCredits(
    workspaceId: string,
    userId: string,
    creditsToUse: number,
    operationType: string,
    operationDetails: Record<string, any> = {}
  ): Promise<boolean> {
    try {
      const { data, error } = await (supabase as any)
        .rpc('use_ai_credits', {
          workspace_uuid: workspaceId,
          user_uuid: userId,
          credits_to_use: creditsToUse,
          operation_type: operationType,
          operation_details: operationDetails
        });

      if (error) {
        console.error('Error using AI credits:', error);
        return false;
      }

      return data === true;
    } catch (error) {
      console.error('Error in useCredits:', error);
      return false;
    }
  }

  /**
   * Purchase additional AI credits
   */
  async purchaseCredits(workspaceId: string, creditsToAdd: number): Promise<boolean> {
    try {
      const { data, error } = await (supabase as any)
        .rpc('purchase_ai_credits', {
          workspace_uuid: workspaceId,
          credits_to_add: creditsToAdd
        });

      if (error) {
        console.error('Error purchasing AI credits:', error);
        return false;
      }

      return data === true;
    } catch (error) {
      console.error('Error in purchaseCredits:', error);
      return false;
    }
  }

  /**
   * Get AI credits usage history
   */
  async getUsageHistory(workspaceId: string, limit: number = 50): Promise<AICreditsUsage[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('ai_credits_usage')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching AI credits usage history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUsageHistory:', error);
      return [];
    }
  }

  /**
   * Check if workspace has enough credits for an operation
   */
  async hasEnoughCredits(workspaceId: string, creditsNeeded: number): Promise<boolean> {
    const status = await this.getCreditsStatus(workspaceId);
    if (!status) return false;
    
    return status.credits_remaining >= creditsNeeded;
  }

  /**
   * Get credits needed for different operations
   */
  getOperationCosts(): Record<string, number> {
    return {
      'chat_message': 1,
      'document_analysis': 5,
      'report_generation': 10,
      'data_processing': 3,
      'ai_summary': 2,
      'quality_check': 4,
      'predictive_analysis': 15,
      'custom_ai_task': 8
    };
  }

  /**
   * Calculate cost for an operation
   */
  calculateOperationCost(operationType: string, complexity: 'simple' | 'medium' | 'complex' = 'medium'): number {
    const baseCosts = this.getOperationCosts();
    const baseCost = baseCosts[operationType] || 1;
    
    const multipliers = {
      simple: 0.5,
      medium: 1.0,
      complex: 2.0
    };
    
    return Math.ceil(baseCost * multipliers[complexity]);
  }

  /**
   * Format credits for display
   */
  formatCredits(credits: number): string {
    if (credits === -1) return 'Unlimited';
    if (credits >= 1000000) return `${(credits / 1000000).toFixed(1)}M`;
    if (credits >= 1000) return `${(credits / 1000).toFixed(1)}K`;
    return credits.toString();
  }

  /**
   * Get time until credits reset
   */
  getTimeUntilReset(resetDate: string): string {
    const now = new Date();
    const reset = new Date(resetDate);
    const diff = reset.getTime() - now.getTime();
    
    if (diff <= 0) return 'Credits reset available';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} until reset`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} until reset`;
    return 'Less than 1 hour until reset';
  }
}

export const aiCreditsService = new AICreditsService(); 